import React, { useState, useEffect, useRef, useMemo } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Snackbar, Alert } from '@mui/material';
import { getProfiles } from 'src/redux/Actions/Tenant';
import { getDocumentListForVehicleTracking } from 'src/redux/Actions/VehicleTracking';
import 'src/style/customMdEditor.css';
import { ReactComponent as Upload } from 'src/Images/icons/common/upload.svg';

export default function CustomMdEditor({
  value,
  onChange,
  themeColor,
  tenantOrganisationId,
  vehicleTrackingNumber,
  placeholder = 'Type your comment here...',
  onFilesChange,
  onMentionSelect,
  maxFileSize = 5 * 1024 * 1024,
}) {
  const [isPreviewToggledInEditor, setIsPreviewToggledInEditor] =
    useState(false);
  const [showFileSizeToast, setShowFileSizeToast] = useState(false);

  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantId = authReducer.userData.tenant?.id;
  const { profileList } = useSelector((state) => state.TenantReducer);
  const { documentListForVehicleTracking } = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });

  const [showHashList, setShowHashList] = useState(false);
  const [hashSearch, setHashSearch] = useState('');
  const [hashPosition, setHashPosition] = useState({ top: 0, left: 0 });

  const editorRef = useRef(null);
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    dispatch(getProfiles(tenantId, ''));
  }, [dispatch, tenantId]);

  const debouncedProfileSearch = useMemo(
    () =>
      debounce((term) => {
        dispatch(getProfiles(tenantId, term));
      }, 400),
    [dispatch, tenantId],
  );

  useEffect(() => {
    if (!showMentionList) return undefined;
    debouncedProfileSearch(mentionSearch);
    return () => {
      debouncedProfileSearch.cancel();
    };
  }, [mentionSearch, showMentionList, debouncedProfileSearch]);

  const debouncedDocumentSearch = useMemo(
    () =>
      debounce((term) => {
        dispatch(
          getDocumentListForVehicleTracking(
            tenantOrganisationId,
            vehicleTrackingNumber,
            term,
          ),
        );
      }, 500),
    [dispatch, tenantOrganisationId, vehicleTrackingNumber],
  );

  useEffect(() => {
    if (!showHashList) return undefined;
    debouncedDocumentSearch(hashSearch);
    return () => {
      debouncedDocumentSearch.cancel();
    };
  }, [hashSearch, showHashList, debouncedDocumentSearch]);

  useEffect(() => {
    if (!editorRef.current) return;
    const textarea = editorRef.current.querySelector('textarea');
    const container = editorRef.current.querySelector('.w-md-editor');
    if (!textarea || !container) return;
    const manualHeight = parseInt(textarea.dataset.manualHeight || '0', 10);
    textarea.style.height = 'auto';
    const contentHeight = textarea.scrollHeight;
    const finalHeight = Math.max(manualHeight, contentHeight);
    textarea.style.height = `${finalHeight}px`;
    container.style.height = `${finalHeight}px`;
  }, [value]);

  useEffect(() => {
    const textarea = editorRef.current?.querySelector('textarea');
    const container = editorRef.current?.querySelector('.w-md-editor');
    let animationFrame;

    const syncHeight = () => {
      if (textarea && container) {
        const currentHeight = textarea.offsetHeight;
        textarea.dataset.manualHeight = currentHeight;
        container.style.height = `${currentHeight}px`;
        animationFrame = requestAnimationFrame(syncHeight);
      }
    };

    const handleMouseDown = () => {
      if (!textarea) return;
      textarea.dataset.manualResize = 'true';
      syncHeight();
      const stopSync = () => {
        cancelAnimationFrame(animationFrame);
        document.removeEventListener('mouseup', stopSync);
      };
      document.addEventListener('mouseup', stopSync);
    };

    if (textarea) textarea.addEventListener('mousedown', handleMouseDown);
    return () => {
      if (textarea) textarea.removeEventListener('mousedown', handleMouseDown);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const handleFileAdd = (newFiles) => {
    const textarea = editorRef.current?.querySelector('textarea');
    const fileArray = Array.from(newFiles);

    fileArray.forEach((file) => {
      if (file.size > maxFileSize) {
        setShowFileSizeToast(true);
        return;
      }

      const cursorPos = textarea
        ? textarea.selectionStart
        : (valueRef.current || '').length;
      const current = valueRef.current || '';
      const before = current.substring(0, cursorPos);
      const after = current.substring(cursorPos);
      const separator = before.length > 0 && !before.endsWith('\n') ? '\n' : '';

      const isImage = file.type.startsWith('image/');

      if (isImage) {
        const blobUrl = URL.createObjectURL(file);
        const token = `![${file.name}](${blobUrl})`;
        const newValue = `${before}${separator}${token}\n\n${after}`;
        onChange(newValue);
        valueRef.current = newValue;

        onFilesChange({
          file,
          imagePlaceholder: token,
          replaceTempPreviewWithUrl: (uploadedUrl) => {
            const cdnMd = `![${file.name}](${uploadedUrl})`;
            const replaced = (valueRef.current || '').replace(token, cdnMd);
            onChange(replaced);
            valueRef.current = replaced;
            URL.revokeObjectURL(blobUrl);
          },
        });
      } else {
        const imagePlaceholder = `<!--"${file.name}"... -->`;
        const newValue = `${before}${separator}${imagePlaceholder}\n\n${after}`;
        onChange(newValue);
        valueRef.current = newValue;

        onFilesChange({
          file,
          imagePlaceholder,
          replaceTempPreviewWithUrl: (uploadedUrl) => {
            const mdLink = `[${file.name}](${uploadedUrl})`;
            const replaced = (valueRef.current || '').replace(
              imagePlaceholder,
              mdLink,
            );
            onChange(replaced);
            valueRef.current = replaced;
          },
        });
      }
    });
  };

  const handleFileZoneClick = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) handleFileAdd(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) handleFileAdd(e.dataTransfer.files);
  };

  const handlePasteOnZone = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = Array.from(items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length) handleFileAdd(files);
  };

  function getCaretCoordinates(element, position) {
    const div = document.createElement('div');
    const style = getComputedStyle(element);
    Array.from(style).forEach((prop) => {
      div.style[prop] = style.getPropertyValue(prop);
    });
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.textContent = element.value.substring(0, position);
    const span = document.createElement('span');
    span.textContent = element.value.substring(position) || '.';
    div.appendChild(span);
    document.body.appendChild(div);
    const coords = { top: span.offsetTop, left: span.offsetLeft };
    document.body.removeChild(div);
    return coords;
  }

  function getDropdownPosition(textarea, cursorPos) {
    const coords = getCaretCoordinates(textarea, cursorPos);
    const bodyRect = bodyRef.current.getBoundingClientRect();
    const textareaRect = textarea.getBoundingClientRect();
    return {
      top:
        textareaRect.top - bodyRect.top + coords.top - textarea.scrollTop + 25,
      left: textareaRect.left - bodyRect.left + coords.left,
    };
  }

  const handlePasteOnEditor = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = Array.from(items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault(); // prevent file being pasted as raw text
      handleFileAdd(files);
    }
  };

  const handleChange = (val) => {
    const textarea = editorRef.current?.querySelector('textarea');
    if (textarea) textarea.dataset.manualResize = 'false';
    onChange(val || '');
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textUntilCursor = textarea.value.substring(0, cursorPos);
    const lastAtIndex = textUntilCursor.lastIndexOf('@');
    const lastHashIndex = textUntilCursor.lastIndexOf('#');
    const isHashActive = lastHashIndex > lastAtIndex && lastHashIndex !== -1;

    if (isHashActive) {
      const term = textUntilCursor.substring(lastHashIndex + 1);
      if (term.includes(' ')) {
        setShowHashList(false);
        return;
      }
      setShowMentionList(false);
      setHashSearch(term);
      setShowHashList(true);
      setHashPosition(getDropdownPosition(textarea, cursorPos));
      return;
    }
    setShowHashList(false);

    // ---- @ mention logic ----
    const charBeforeAt =
      lastAtIndex > 0 ? textUntilCursor[lastAtIndex - 1] : '';
    const isAtStartOfWord = lastAtIndex === 0 || /\s/.test(charBeforeAt);

    if (lastAtIndex === -1 || !isAtStartOfWord) {
      setShowMentionList(false);
      return;
    }

    const term = textUntilCursor.substring(lastAtIndex + 1);
    if (/\s/.test(term)) {
      setShowMentionList(false);
      return;
    }

    setMentionSearch(term);
    setShowMentionList(true);
    setMentionPosition(getDropdownPosition(textarea, cursorPos));
  };

  const insertMentionItem = (profile) => {
    const textarea = editorRef.current?.querySelector('textarea');
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const currentValue = textarea.value;
    const textBeforeCursor = currentValue.substring(0, cursorPos);
    const textAfterCursor = currentValue.substring(cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex === -1) return;
    const mentionText = `@${profile.name} `;
    const newMessage =
      textBeforeCursor.substring(0, lastAtIndex) +
      mentionText +
      textAfterCursor;
    onChange(newMessage);
    valueRef.current = newMessage;
    onMentionSelect(profile);
    setShowMentionList(false);
    requestAnimationFrame(() => {
      textarea.focus();
      const newCursorPos = lastAtIndex + mentionText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  };

  const insertHashItem = (item) => {
    const markdownLink = `[${item.object_code}](/${item.object_url}) `;
    const textarea = editorRef.current?.querySelector('textarea');
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const currentValue = textarea.value;
    const textBeforeCursor = currentValue.substring(0, cursorPos);
    const textAfterCursor = currentValue.substring(cursorPos);
    const lastHashIndex = textBeforeCursor.lastIndexOf('#');
    if (lastHashIndex === -1) return;
    const newMessage =
      textBeforeCursor.substring(0, lastHashIndex) +
      markdownLink +
      textAfterCursor;
    onChange(newMessage);
    valueRef.current = newMessage;
    setShowHashList(false);
    requestAnimationFrame(() => {
      textarea.focus();
      const newCursorPos = lastHashIndex + markdownLink.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  };

  return (
    <div className="conversation-md-editor editor-only">
      <div
        ref={editorRef}
        className="md-editor-main"
        data-color-mode="light"
        style={{ '--theme-color': themeColor }}
      >
        <div className="conversation-md-header">
          <div className="conversation-md-tabs">
            <button
              type="button"
              className={!isPreviewToggledInEditor ? 'active' : ''}
              onClick={() => setIsPreviewToggledInEditor(false)}
            >
              Write
            </button>
            <button
              type="button"
              className={isPreviewToggledInEditor ? 'active' : ''}
              onClick={() => setIsPreviewToggledInEditor(true)}
            >
              Preview
            </button>
          </div>
        </div>

        <div
          ref={bodyRef}
          className="github-md-body"
          style={{ position: 'relative' }}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => e.preventDefault()}
        >
          <MDEditor
            value={value}
            onChange={handleChange}
            preview={isPreviewToggledInEditor ? 'preview' : 'edit'}
            visibleDragbar={false}
            hideToolbar={false}
            extraCommands={[]}
            textareaProps={{
              placeholder,
              style: { minHeight: 24 },
              onPaste: handlePasteOnEditor,
              onDrop: handleDrop,
              onDragOver: (e) => e.preventDefault(),
              onDragEnter: (e) => e.preventDefault(),
            }}
            height="auto"
          />

          {showMentionList && profileList.length > 0 && (
            <div
              className="mention-dropdown"
              style={{
                position: 'absolute',
                top: mentionPosition.top,
                left: mentionPosition.left,
                zIndex: 10,
              }}
            >
              {profileList.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  className="mention-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMentionItem(profile);
                  }}
                >
                  <div className="mention-name">{profile.name}</div>
                </button>
              ))}
            </div>
          )}

          {showHashList && documentListForVehicleTracking.length > 0 && (
            <div
              className="mention-dropdown"
              style={{
                position: 'absolute',
                top: hashPosition.top,
                left: hashPosition.left,
                zIndex: 10,
              }}
            >
              {documentListForVehicleTracking.map((item) => (
                <button
                  key={item.object_uuid}
                  type="button"
                  className="mention-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertHashItem(item);
                  }}
                >
                  <div className="mention-name">{item.object_code}</div>
                  <div className="mention-team">{item.created_by}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="md-file-drop-zone text-sm"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onPaste={handlePasteOnZone}
        onClick={handleFileZoneClick}
        onKeyDown={(e) => e.key === 'Enter' && handleFileZoneClick()}
        role="button"
        tabIndex={0}
      >
        <Upload className="icon-size-md" />
        <span className="md-file-drop-text">
          Paste, drop, or click to add files
        </span>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="d-none"
        onChange={handleFileInputChange}
      />

      <Snackbar
        open={showFileSizeToast}
        autoHideDuration={3000}
        onClose={() => setShowFileSizeToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="custom-snackbar"
      >
        <Alert
          onClose={() => setShowFileSizeToast(false)}
          severity="error"
          className="custom-snackbar-alert"
        >
          File size exceeds the 5MB limit.
        </Alert>
      </Snackbar>
    </div>
  );
}
